/**
 Variant Lead Time JavaScript
 Dynamically updates lead time display when customers select different variants
 
Developer Notes:
    - Author: Alec Hewstone (Kubix Media)
    - Date: 11/11/2025

Architecture Overview:
    - This block uses a reusable pattern for variant-aware functionality that could be 
    abstracted into a shared utility if NCF requires more variant-aware blocks in future

Pattern Overview:
    1. Liquid pre-renders all variant data in <script type="application/json">
    2. data-initial-variant attribute stores the selected variant ID
    3. Listens for theme's native 'on:variant:change' event
    4. Updates DOM based on variant-specific data

Potential Future Abstractions:
    If building similar blocks (variant stock levels, custom pricing, delivery notes):
    - Consider creating a generic variant-change-handler.js utility
    - Core pattern: registerHandler(selector, updateFn, initFn)
    - Shared event listener with block-specific update logic

Decision:
    - Kept specific for now to avoid premature over-engineering
    - Refactor when we have 2-3 similar blocks

@see snippets/lead-time.liquid for Liquid implementation
@see sections/main-product.liquid for block schema
 */

(() => {
  const block = document.querySelector('[data-variant-lead-time]');
  if (!block) {
    return;
  }

  const contentEl = block.querySelector('[data-variant-lead-time-content]');
  const dataEl = block.querySelector('script[data-variant-lead-time-json]');

  if (!contentEl || !dataEl) {
    return;
  }

  let config;
  try {
    config = JSON.parse(dataEl.textContent);
  } catch (error) {
    return;
  }

  const updateLeadTime = (variantId) => {
    const variantEntry = config.variants.find((entry) => Number(entry.id) === Number(variantId));
    let value = variantEntry?.metafield || config.productFallback || config.fallbackText || '';
    value = typeof value === 'string' ? value.trim() : value;

    if (value) {
      block.hidden = false;
      contentEl.innerHTML = value;
      return;
    }

    contentEl.innerHTML = '';

    if (config.hideWhenBlank) {
      block.hidden = true;
    } else {
      block.hidden = false;
    }
  };

  document.addEventListener('on:variant:change', (event) => {
    const variantId = event.detail?.variant?.id;
    if (!variantId) return;

    updateLeadTime(Number(variantId));
  });

  const initialVariantAttr = block.dataset.initialVariant;
  if (initialVariantAttr) {
    updateLeadTime(Number(initialVariantAttr));
  } else {
    const firstVariantWithValue = config.variants.find((entry) => entry.metafield);
    if (firstVariantWithValue) {
      updateLeadTime(Number(firstVariantWithValue.id));
    }
  }
})();